import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import './launchpad.css';
import AuthService from '../../../api/services/AuthService';
import { resolvePublicUploadUrl } from '../../../api/apiConfig/apiConfig';
import { alertErrorMessage, alertSuccessMessage } from '../../../customComponents/CustomAlertMessage';
const poolIconMap = {
    'USD1': 'usd_commit_icon.svg',
    'GUSD': 'usd_commit_icon2.svg',
    'USDT': 'usd_commit_icon3.svg'
};

const LAUNCHPAD_LOGO_FALLBACK = '/images/AnnouncementImg/eul_coin.png';

const resolveImg = (url) => resolvePublicUploadUrl(url) || LAUNCHPAD_LOGO_FALLBACK;

const resolveCurrencyIcon = (url) => (url ? resolvePublicUploadUrl(url) : '');

const formatPhaseTime = (value) => {
    if (!value) return '--';
    const d = new Date(value);
    if (isNaN(d.getTime())) return String(value);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const parseAmount = (n) => {
    if (n == null || n === '') return 0;
    if (typeof n === 'object') {
        const raw = n.$numberDecimal ?? n.value ?? n.amount ?? 0;
        return Number(raw) || 0;
    }
    const cleaned = String(n).replace(/,/g, '').trim();
    const num = Number(cleaned);
    return Number.isFinite(num) ? num : 0;
};

/** Thousand separators with commas, e.g. 100000000 → 100,000,000 */
const formatNum = (n) => {
    const num = parseAmount(n);
    return num.toLocaleString('en-US', {
        maximumFractionDigits: 8,
    });
};

const LaunchpadDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('Founder');
    const [detail, setDetail] = useState(null);
    const [showTradeModal, setShowTradeModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [tradeAmount, setTradeAmount] = useState('');
    const [balance, setBalance] = useState(null);
    const [balanceLoading, setBalanceLoading] = useState(false);
    const [tradeSubmitting, setTradeSubmitting] = useState(false);

    const fetchDetail = async () => {
        if (!id) return;
        try {
            const result = await AuthService.getLaunchpadById(id);
            if (result?.success && result?.data) {
                setDetail(result.data);
            } else {
                setDetail(null);
            }
        } catch (err) {
            setDetail(null);
        }
    };

    useEffect(() => {
        let isMounted = true;
        if (isMounted) fetchDetail();
        return () => { isMounted = false; };
    }, [id]);

    const tokenSymbol = detail?.tokenSymbol || detail?.baseCurrency?.short_name || '---';
    const acceptedCurrency = detail?.acceptedCurrency || detail?.quoteCurrency?.short_name || 'USDT';
    const tokenPrice = detail?.tokenPrice ?? 0;
    const pd = detail?.projectDetails || {};
    const quoteCurrencyId = detail?.quoteCurrency?._id || detail?.quoteCurrency?.currency_id
        || detail?.quoteCurrencyId || detail?.quote_currency_id || detail?.acceptedCurrencyId || '';
    const estimatedTokens = (parseFloat(tradeAmount) || 0) * (Number(tokenPrice) || 0);

    const fetchBalance = async (currencyId) => {
        const cid = currencyId || quoteCurrencyId;
        if (!cid) return;
        setBalanceLoading(true);
        try {
            const result = await AuthService.getLaunchpadUserBalance(cid, 'earning');
            const data = result?.data;
            const rawBal = (data && typeof data === 'object')
                ? (data.balance ?? data.amount ?? 0)
                : (data ?? 0);
            const bal = (rawBal && typeof rawBal === 'object')
                ? (rawBal.$numberDecimal ?? rawBal.value ?? 0)
                : rawBal;
            setBalance(Number(bal) || 0);
        } catch (err) {
            setBalance(0);
        } finally {
            setBalanceLoading(false);
        }
    };

    useEffect(() => {
        if (quoteCurrencyId) fetchBalance();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quoteCurrencyId]);

    const handleOpenTrade = () => {
        if (!localStorage.getItem('token')) {
            navigate('/login');
            return;
        }
        setTradeAmount('');
        setShowTradeModal(true);
        fetchBalance();
    };

    const getAmountError = (value) => {
        const amount = parseFloat(value);
        const minBuy = Number(detail?.minBuy);
        const maxBuy = Number(detail?.maxBuy);
        if (value === '' || value === null || value === undefined) return '';
        if (isNaN(amount) || amount <= 0) return 'Amount must be greater than 0.';
        if (minBuy && amount < minBuy) return `Minimum amount is ${formatNum(minBuy)} ${acceptedCurrency}.`;
        if (maxBuy && amount > maxBuy) return `Maximum amount is ${formatNum(maxBuy)} ${acceptedCurrency}.`;
        if (balance != null && amount > balance) return 'Insufficient balance.';
        return '';
    };

    const amountError = getAmountError(tradeAmount);

    const handleProceedToConfirm = () => {
        const err = getAmountError(tradeAmount === '' ? '0' : tradeAmount);
        if (err) {
            alertErrorMessage(err);
            return;
        }
        setShowTradeModal(false);
        setShowConfirmModal(true);
    };

    const handleConfirmBuy = async () => {
        const amount = parseFloat(tradeAmount);
        const launchpadId = detail?._id || id;
        if (!launchpadId) {
            alertErrorMessage('Launchpad information not available.');
            return;
        }
        setTradeSubmitting(true);
        try {
            const result = await AuthService.buyToken(launchpadId, amount);
            if (result?.success) {
                alertSuccessMessage(result?.message || 'Investment successful');
                setShowConfirmModal(false);
                setTradeAmount('');
                fetchDetail();
            } else {
                alertErrorMessage(result?.message || 'Investment failed.');
            }
        } catch (err) {
            alertErrorMessage('Something went wrong. Please try again.');
        } finally {
            setTradeSubmitting(false);
        }
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 'Founder':
                return (
                    <>
                        <div className="founder_info mb-4">
                            <div className="founder_header d-flex align-items-center gap-3 mb-3">
                                <div className="founder_img_placeholder">
                                    {pd.founderImage && <img src={resolveImg(pd.founderImage)} alt={pd.founderName || 'Founder'} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover', display: 'block' }} onError={(e) => { e.target.style.display = 'none'; }} />}
                                </div>
                                <h4 className="m-0 fw-bold">{pd.founderName || '---'}</h4>
                            </div>
                            <p className="content_text">{pd.aboutFounder || '---'}</p>
                        </div>
                        <div className="highlight_info">
                            <h4 className="fw-bold mb-3">Highlight/Vision</h4>
                            <p className="content_text">{pd.vision || '---'}</p>
                        </div>
                    </>
                );
            case 'Highlight/Vision':
                return (
                    <div className="highlight_info">
                        <h4 className="fw-bold mb-3">Highlight/Vision</h4>
                        <p className="content_text">{pd.vision || '---'}</p>
                    </div>
                );
            case 'Token Economics':
                return (
                    <div className="economics_info">
                        <h4 className="fw-bold mb-3">Token Economics</h4>
                        <p className="content_text">{pd.tokenEconomics || '---'}</p>
                    </div>
                );
            case 'Technical Overview':
                return (
                    <div className="tech_info">
                        <h4 className="fw-bold mb-3">Technical Overview</h4>
                        <p className="content_text">{pd.technicalOverview || '---'}</p>
                    </div>
                );
            /* case 'Token Allocation':
                return (
                    <div className="allocation_info">
                        <h4 className="fw-bold mb-3">Token Allocation</h4>
                        <p className="content_text">Total Supply: 10,000,000,000 IMU.<br />Community & Ecosystem: 45%<br />Core Team & Advisors: 20%<br />Treasury: 25%<br />Launchpad: 10%</p>
                    </div>
                ); */
            case 'Risk Warning':
                return (
                    <div className="risk_info">
                        <h4 className="fw-bold mb-3">Risk Warning</h4>
                        <p className="content_text">{pd.riskWarning || '---'}</p>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="launchpad_wrapper_main">
            <div className="container">
                {/* Header Row */}
                <div className="detail_header_row">
                    <div className="breadcrumb">
                        <Link to="/launchpad" className="text-muted text-decoration-none">Launchpad</Link>
                        <i className="ri-arrow-right-s-line mx-2 text-muted"></i>
                        <span className="text-dark">{tokenSymbol}</span>
                    </div>
                    <div className="header_actions">
                        {/* <button className="action_btn"><img src='/images/launchpad-img/share_icon.svg' alt='share'/> Share</button>
                        <button className="action_btn"><img src='/images/launchpad-img/file_records_icon.svg' alt='share'/>  Commitment Records</button>
                        <button className="action_btn"><img src='/images/launchpad-img/file_records_icon2.svg' alt='share'/>  Allocation Records</button> */}
                    </div>
                </div>

                {/* Main Content Layout */}
                <div className="detail_content_layout">

                    {/* Left Column */}
                    <div className="detail_left_col">
                        <div className="project_header_info">
                            <div className="d-flex align-items-center gap-3 flex-grow-1">
                                <div className="project_icon imu">
                                    <img src={resolveImg(detail?.baseCurrency?.icon_path || detail?.baseCurrency?.iconPath || detail?.logo)} alt={tokenSymbol} onError={(e) => { e.target.src = LAUNCHPAD_LOGO_FALLBACK; }} />
                                </div>
                                <h2 className="detail_project_name">{tokenSymbol}</h2>
                                <span className="status_ended_badge">{detail?.status || '---'}</span>
                            </div>
                            <button className="trade_btn_dark" onClick={handleOpenTrade}>Trade</button>
                        </div>

                        <div className="project_description">
                            <p>{detail?.description || '---'}</p>
                        </div>

                        <div className="project_links">
                            <a href={detail?.website || '#'} target="_blank" rel="noreferrer" className="pill_link"><img src='/images/launchpad-img/site_icon.svg' alt='website' /> Website</a>
                            {/* <a href="#" className="pill_link"><img src='/images/launchpad-img/activit_icon.svg' alt='activity '/> Activity announcement</a>
                            <a href="#" className="pill_link"><img src='/images/launchpad-img/faq_icon.svg' alt='faq'/> FAQ</a>
                            <a href="#" className="pill_link"><img src='/images/launchpad-img/twitter_icon.svg' alt='twitter'/> Twitter</a> */}
                        </div>

                        <div className="project_stats_row">
                            <div className="stat_item">
                                <span className="label">Total Allocation</span>
                                <span className="value">{formatNum(detail?.totalSupply)} {tokenSymbol}</span>
                            </div>
                            <div className="stat_item">
                                <span className="label">Number of Participants</span>
                                <span className="value">{formatNum(detail?.totalParticipants)}</span>
                            </div>
                        </div>

                        <div className="event_phases_sec">
                            <h3>Event Phases</h3>
                            <div className="timeline">
                                <div className="timeline_item completed">
                                    <div className="marker"><i className="ri-check-line"></i></div>
                                    <div className="content">
                                        <h4>Warm Up</h4>
                                        <p>{formatPhaseTime(detail?.startTime)}</p>
                                    </div>
                                </div>
                                <div className="timeline_line completed"></div>
                                <div className="timeline_item completed">
                                    <div className="marker"><i className="ri-check-line"></i></div>
                                    <div className="content">
                                        <h4>Subscription Starts</h4>
                                        <p>{formatPhaseTime(detail?.startTime)}</p>
                                    </div>
                                </div>
                                <div className="timeline_line completed"></div>
                                <div className="timeline_item completed">
                                    <div className="marker"><i className="ri-check-line"></i></div>
                                    <div className="content">
                                        <h4>Subscription Ends</h4>
                                        <p>{formatPhaseTime(detail?.endTime)}</p>
                                    </div>
                                </div>
                                {/* <div className="timeline_line"></div>
                                <div className="timeline_item">
                                    <div className="marker">4</div>
                                    <div className="content">
                                        <h4>Distribution</h4>
                                        <p>2026/01/22 13:30</p>
                                    </div>
                                </div> */}
                            </div>
                        </div>

                        <div className="launchpad_details_table_sec">
                            <h3>Launchpad Details</h3>
                            <div className="details_table">
                                <div className="table_row">
                                    <div className="table_col_left">Total Supply</div>
                                    <div className="table_col_right">{formatNum(detail?.totalSupply)} {tokenSymbol}</div>
                                </div>
                                <div className="table_row">
                                    <div className="table_col_left">Total Allocation for Launchpad</div>
                                    <div className="table_col_right">{formatNum(detail?.availableTokens)} {tokenSymbol}</div>
                                </div>
                                <div className="table_row">
                                    <div className="table_col_left">Allocation for {acceptedCurrency} Pool</div>
                                    <div className="table_col_right">{formatNum(detail?.availableTokens)} {tokenSymbol}</div>
                                </div>
                                <div className="table_row">
                                    <div className="table_col_left">Soft Cap</div>
                                    <div className="table_col_right">{formatNum(detail?.softCap)} {acceptedCurrency}</div>
                                </div>
                                <div className="table_row">
                                    <div className="table_col_left">Hard Cap</div>
                                    <div className="table_col_right">{formatNum(detail?.hardCap)} {acceptedCurrency}</div>
                                </div>
                                <div className="table_row">
                                    <div className="table_col_left">Total Commitment({acceptedCurrency})</div>
                                    <div className="table_col_right">{formatNum(detail?.totalRaised)} {acceptedCurrency}</div>
                                </div>
                                <div className="table_row">
                                    <div className="table_col_left">Min Buy({acceptedCurrency})</div>
                                    <div className="table_col_right">{formatNum(detail?.minBuy)} {acceptedCurrency}</div>
                                </div>
                                <div className="table_row">
                                    <div className="table_col_left">Cap per Subscriber({acceptedCurrency})</div>
                                    <div className="table_col_right">{formatNum(detail?.maxBuy)} {acceptedCurrency}</div>
                                </div>
                            </div>
                        </div>

                        <div className="project_details_tab_sec">
                            <h3>Project Details</h3>
                            <div className="project_details_container">
                                <div className="sidebar_menu">
                                    <div className={`menu_item ${activeTab === 'Founder' ? 'active' : ''}`} onClick={() => setActiveTab('Founder')}>Founder</div>
                                    <div className={`menu_item ${activeTab === 'Highlight/Vision' ? 'active' : ''}`} onClick={() => setActiveTab('Highlight/Vision')}>Highlight/Vision</div>
                                    <div className={`menu_item ${activeTab === 'Token Economics' ? 'active' : ''}`} onClick={() => setActiveTab('Token Economics')}>Token Economics</div>
                                    <div className={`menu_item ${activeTab === 'Technical Overview' ? 'active' : ''}`} onClick={() => setActiveTab('Technical Overview')}>Technical Overview</div>
                                    {/* <div className={`menu_item ${activeTab === 'Token Allocation' ? 'active' : ''}`} onClick={() => setActiveTab('Token Allocation')}>Token Allocation</div> */}
                                    <div className={`menu_item ${activeTab === 'Risk Warning' ? 'active' : ''}`} onClick={() => setActiveTab('Risk Warning')}>Risk Warning</div>
                                </div>
                                <div className="content_area">
                                    {renderTabContent()}
                                </div>
                            </div>
                        </div>

                    </div>

                    {/* Right Column */}
                    <div className="detail_right_col">
                        <div className="pool_card detail_pool_card">
                            <div className="pool_title">
                                <div className="d-flex align-items-center gap-2">
                                    <div className="pool_icon">
                                        <img src={detail?.quoteCurrency?.icon_path ? resolveCurrencyIcon(detail.quoteCurrency.icon_path) : `/images/launchpad-img/${poolIconMap[acceptedCurrency] || 'usd_commit_icon3.svg'}`} alt={acceptedCurrency} onError={(e) => { e.target.src = `/images/launchpad-img/${poolIconMap[acceptedCurrency] || 'usd_commit_icon3.svg'}`; }} />
                                    </div>
                                    <h4>Commit {acceptedCurrency}</h4>
                                </div>
                            </div>

                            <div className="pool_stats_row">
                                <div className="pool_stat">
                                    <p className="stat_label">Allocation</p>
                                    <p className="stat_value">{formatNum(detail?.availableTokens)} <span>{tokenSymbol}</span></p>
                                </div>
                                <div className="pool_stat text-end">
                                    <p className="stat_label">Commitment</p>
                                    <p className="stat_value">{formatNum(detail?.totalRaised)} <span>{acceptedCurrency}</span></p>
                                </div>
                            </div>

                            <div className="pool_stat mt-3">
                                <p className="stat_label">Cap per Subscriber</p>
                                <p className="stat_value">{formatNum(detail?.maxBuy)} <span>{acceptedCurrency}</span></p>
                            </div>

                            <div className="pool_summary mt-4">
                                <div className="summary_row">
                                    <p className="summary_label">Subscription Price</p>
                                    <p className="summary_value">1 {tokenSymbol} = {formatNum(tokenPrice)} {acceptedCurrency}</p>
                                </div>
                                <div className="summary_row mt-2">
                                    <p className="summary_label">Number of Participants</p>
                                    <p className="summary_value">{formatNum(detail?.totalParticipants)}</p>
                                </div>
                            </div>
                        </div>

                        <div className="pool_card detail_pool_card mt-4 d-none">
                            <div className="pool_title">
                                <div className="d-flex align-items-center gap-2">
                                    <div className="pool_icon">
                                        <img src={detail?.quoteCurrency?.icon_path ? resolveCurrencyIcon(detail.quoteCurrency.icon_path) : `/images/launchpad-img/${poolIconMap[acceptedCurrency] || 'usd_commit_icon3.svg'}`} alt={acceptedCurrency} onError={(e) => { e.target.src = `/images/launchpad-img/${poolIconMap[acceptedCurrency] || 'usd_commit_icon3.svg'}`; }} />
                                    </div>
                                    <h4>Commit {acceptedCurrency}</h4>
                                </div>
                            </div>

                            <div className="pool_stats_row">
                                <div className="pool_stat">
                                    <p className="stat_label">Allocation</p>
                                    <p className="stat_value">{formatNum(detail?.availableTokens)} <span>{tokenSymbol}</span></p>
                                </div>
                                <div className="pool_stat text-end">
                                    <p className="stat_label">Commitment</p>
                                    <p className="stat_value">{formatNum(detail?.totalRaised)} <span>{acceptedCurrency}</span></p>
                                </div>
                            </div>

                            <div className="pool_stat mt-3">
                                <p className="stat_label">Cap per Subscriber</p>
                                <p className="stat_value">{formatNum(detail?.maxBuy)} <span>{acceptedCurrency}</span></p>
                            </div>

                            <div className="pool_summary mt-4">
                                <div className="summary_row">
                                    <p className="summary_label">Subscription Price</p>
                                    <p className="summary_value">1 {tokenSymbol} = {formatNum(tokenPrice)} {acceptedCurrency}</p>
                                </div>
                                <div className="summary_row mt-2">
                                    <p className="summary_label">Number of Participants</p>
                                    <p className="summary_value">{formatNum(detail?.totalParticipants)}</p>
                                </div>
                            </div>
                        </div>

                    </div>

                </div>
            </div>

            {showTradeModal && (
                <div className="launchpad_modal_overlay">
                    <div className="launchpad_modal_content">
                        <div className="launchpad_modal_header">
                            <h2>Trade {tokenSymbol}</h2>
                            <button className="close_modal_btn" onClick={() => setShowTradeModal(false)}>
                                <i className="ri-close-line"></i>
                            </button>
                        </div>
                        <div className="launchpad_modal_body mt-4">
                            <div className="summary_row launchpad-trade-row">
                                <span className="summary_label">Available Balance</span>
                                <span className="summary_value">{balanceLoading ? 'Loading...' : `${formatNum(balance)} ${acceptedCurrency}`}</span>
                            </div>
                            <label className="summary_label launchpad-trade-label">Amount ({acceptedCurrency})</label>
                            <input
                                type="number"
                                min="0"
                                value={tradeAmount}
                                onChange={(e) => setTradeAmount(e.target.value)}
                                placeholder={`Enter amount in ${acceptedCurrency}`}
                                className="launchpad-trade-input"
                            />
                            {amountError && (
                                <p className="launchpad-trade-error">{amountError}</p>
                            )}
                            <div className="summary_row launchpad-trade-row mt-16">
                                <span className="summary_label">You will receive</span>
                                <span className="summary_value">{formatNum(estimatedTokens)} {tokenSymbol}</span>
                            </div>
                            <button
                                className="trade_btn_dark mt-4 launchpad-trade-btn"
                                disabled={!tradeAmount || !!amountError}
                                onClick={handleProceedToConfirm}
                            >
                                Trade
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showConfirmModal && (
                <div className="launchpad_modal_overlay">
                    <div className="launchpad_modal_content">
                        <div className="launchpad_modal_header">
                            <h2>Confirm Investment</h2>
                            <button className="close_modal_btn" onClick={() => setShowConfirmModal(false)}>
                                <i className="ri-close-line"></i>
                            </button>
                        </div>
                        <div className="launchpad_modal_body mt-4">
                            <div className="summary_row launchpad-trade-row mb-12">
                                <span className="summary_label">Project</span>
                                <span className="summary_value">{tokenSymbol}</span>
                            </div>
                            <div className="summary_row launchpad-trade-row mb-12">
                                <span className="summary_label">Amount</span>
                                <span className="summary_value">{formatNum(parseFloat(tradeAmount) || 0)} {acceptedCurrency}</span>
                            </div>
                            <div className="summary_row launchpad-trade-row mb-12">
                                <span className="summary_label">Subscription Price</span>
                                <span className="summary_value">1 {tokenSymbol} = {formatNum(tokenPrice)} {acceptedCurrency}</span>
                            </div>
                            <div className="summary_row launchpad-trade-row">
                                <span className="summary_label">You will receive (approx.)</span>
                                <span className="summary_value">{formatNum(estimatedTokens)} {tokenSymbol}</span>
                            </div>
                            <div className="d-flex gap-3 mt-4">
                                <button
                                    className="action_btn"
                                    style={{ flex: 1 }}
                                    disabled={tradeSubmitting}
                                    onClick={() => { setShowConfirmModal(false); setShowTradeModal(true); }}
                                >
                                    Back
                                </button>
                                <button
                                    className="trade_btn_dark"
                                    style={{ flex: 1 }}
                                    onClick={handleConfirmBuy}
                                    disabled={tradeSubmitting}
                                >
                                    {tradeSubmitting ? 'Processing...' : 'Confirm'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LaunchpadDetail;
